<template>
  <!-- New Card Design -->
  <div :id="'card_' + media.id" :data-test="'card_' + media.id" class="media-card">
    <div v-if="$auth.auth && $auth.hasRole('super-admin')" style="text-align: end;">
      <i class="fas fa-pen-to-square pointer hovered" style="position: absolute; margin-top: 9px; margin-left: -25px; font-size: larger; color: #5c6d80; z-index: 2;"
      @click="goToEdit(media)"></i>
    </div>
    <article class="card card-hover media-card-section shadow-lg" @click="visitMedia(media)">
      <a class="media-card-thumbnail d-flex align-items-center justify-content-center media-3 rounded-top p-0">
        <MediaThumbnail :media="media" />
      </a>
      <div class="card-body p-3 align-middle" style="min-height: 140px;">
        <h2 :title="media.title" class="h5 nav-heading my-2 text-center media-card-header-text">
          <a role="button">{{ media.title }}</a>
        </h2>
        <p v-if="media.network && columns !== '12'" class="card-text">
          <i class="fas fa-user-group"></i>
          {{ media.network.short_name }}
          <span v-if="media.type === 'video' && media.vimeo_error_status" class="badge bg-danger ms-4">
            {{ $t('network.errorUplaodingVimeo') }}
          </span>
          <span v-else-if="media.type === 'video' && !media.vimeo_sync_status" class="badge bg-info ms-4">
            {{ $t('network.uploadInProgress') }}
          </span>
        </p>
      </div>
      <div class="card-footer media-card-footer">
        <div class="media-2 text-left flex-nowrap min-height-65">
          <div class="row g-0">
            <div v-if="media.type" class="col-xl-6 col-lg-6 col-md-12 col-sm-12 py-1">
              <a v-if="media.type === 'video'" class="meta-link fs-xs">
                <i class="fas fa-video ms"></i>
                <span class="ms-1 align-middle">{{ $t('network.mediaTypes.video') }}</span>
              </a>
              <a v-if="media.type === 'file'" class="meta-link fs-xs">
                <i class="fas fa-file ms"></i>
                <span class="ms-1 align-middle">{{ $t('network.file') }}</span>
              </a>
              <a v-if="media.isCollection" class="meta-link fs-xs">
                <i class="fas fa-folder ms"></i>
                <span class="ms-1 align-middle">{{ $t('network.collection') }}</span>
              </a>
            </div>
            <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 py-1">
              <div v-if="(media.type === 'video' && media.duration_seconds) || (media.isCollection && media.collection_items)">
                <a v-if="media.type === 'video' && media.duration_seconds" class="meta-link fs-xs" href="#">
                  <i class="fas fa-clock ms"></i>
                  <span class="ms-1 align-middle">{{ durationForHumans(media.duration_seconds) }}</span>
                </a>
                <a v-if="media.isCollection && media.collection_items" class="meta-link fs-xs" href="#">
                  <i class="fas fa-folder-tree ms"></i>
                  <span class="ms-1 align-middle">{{ media.collection_items }}</span>
                </a>
              </div>
              <div v-else class="ms-1"></div>
            </div>
            <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 py-1">
              <a class="meta-link fs-xs" href="#">
                <i class="fas fa-calendar-alt"></i>
                <span v-if="media.publish_date" class="ms-1 align-middle">{{ formatDate(media.publish_date) }}</span>
                <span v-else>&nbsp;{{ $t('not-available') }}</span>
              </a>
            </div>
          </div>
        </div>
        <!--Multiple presenters-->
        <div v-if="media.presenters.length > 1" class="row g-0 mt-2 mx-n1" style="min-height: 42px;">
          <div class="dropdown w-100">
            <div 
              :id="'anchor' + index" 
              ref="anchor"
              class="row g-0 w-100 dropbtn" 
              @mouseenter="reinitilizePopper()"
            >
              <!-- Avatar image column -->
              <div class="col-xxl-4 col-lg-4 col-md-3 d-none d-md-block">
                <div class="avatar-group avatar-group-overlapped">
                  <!--Only show first presenter image-->
                  <div class="avatar">
                    <img :src="media.presenters[0].image" :alt="media.presenters[0].name" class="avatar-img rounded-circle" />
                  </div>
                  <!--Only display additional avatar indicators for md screens and up-->
                  <div v-if="media.presenters.length > 1" class="avatar d-none d-md-block"
                    style="margin-top: 17px; margin-left: -10px; z-index: 2;">
                    <span class="avatar-text avatar-text-indigo rounded-circle" style="height: 2rem; width: 2rem;">
                      <span class="initial-wrap">
                        <span>+{{ parseInt(media.presenters.length - 1) }}</span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <!-- Text info column -->
              <div class="col-xxl-8 col-lg-8 col-md-9 col-sm-12 text-start custom-margin-xl-multiple">
                <div v-if="media.presenters.length > 0">
                  <p class="presenter-facepile-text">{{ $t('CardFull.presented-by') }}</p>
                  <p class="presenter-facepile-text text-truncate">
                    {{ media.presenters[0].name }}
                  </p>
                  <p v-if="media.presenters.length > 1" class="presenter-facepile-text">
                    {{ $t('and-others', { num: parseInt(media.presenters.length - 1) }) }}
                  </p>
                </div>
              </div>
            </div>
            <!--Dropdown content-->
            <ul :id="'anchordropdown' + index" ref="anchordropdown" class="list-group dropdown-content">
              <li v-for="(p, i) in media.presenters.slice(1, media.presenters.length)" :key="'presenter-' + i" class="list-group-item">
                <span class="me-2">
                  <div :key="'presenter-' + i" class="avatar avatar-small">
                    <img :src="p.image" :alt="p.name" class="avatar-img rounded-circle" height="35" width="35" />
                  </div>
                </span>
                <span class="text-truncate d-inline-block" style="max-width: 180px; vertical-align: middle;">{{ p.name }}</span>
              </li>
            </ul>
          </div>
        </div>
        <!--Single presenter-->
        <div v-else class="row g-0 mt-2 mx-n1" style="min-height: 42px;">
          <div class="col-md-3 d-none d-md-block">
            <div class="avatar-group avatar-group-overlapped mr-40 mt-25">
              <div v-for="(p, i) in media.presenters.slice(0, 1)" :key="'presenter-' + i" class="avatar">
                <img :src="p.image" :alt="p.name" class="avatar-img rounded-circle" />
              </div>
            </div>
          </div>
          <div class="col-md-9 col-sm-12 mt-1 custom-margin-xl-single">
            <span v-for="(p, i) in media.presenters.slice(0, 1)" :key="'presenter-text-' + i">
              <p class="presenter-facepile-text">{{ $t('CardFull.presented-by') }} {{ p.name }}</p>
            </span>
          </div>
        </div>
      </div>
    </article>
  </div>
</template>
<script>
import { durationForHumans } from '@/lib/util.js';
import MediaThumbnail from '@/components/commons/Media/Thumbnail.vue';
import MediaThumbnailGenerator from '@/services/MediaThumbnailGenerator.js';
import { createPopper } from '@popperjs/core';
import moment from 'moment';

export default {
  name: 'CardFull',
  components: {
    MediaThumbnail,
  },
  props: {
    media: {
      type: Object,
      default: null,
    },
    columns: {
      type: Array,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    network: {
      type: [Object, Array],
      default: null,
    },
  },
  data() {
    return {
      popper: {},
    };
  },
  computed: {
    columnClass: function () {
      if (this.columns === 3) return 'col-xl-3 col-lg-3 col-md-3 col-sm-6';
      if (this.columns === 6) return 'col-xl-6 col-lg-6 col-md-6 col-sm-6';
      return 'col-' + this.columns;
    },
  },
  mounted() {
    this.initPoppers();
  },
  methods: {
    // Initializes the Popover feature for ALL presenter dropdowns (for certain cases when we exceed >4 presenters)
    initPoppers() {
      // TODO: destroy any registered poppers
      let anchorEl = this.$refs.anchor;
      let anchordropdownEl = this.$refs.anchordropdown;

      // initialize popper's
      if (anchorEl && anchordropdownEl) {
        this.popper = createPopper(anchorEl, anchordropdownEl, {
          placement: 'auto',
        });
      }
    },
    reinitilizePopper() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.popper.update();
        }, 100);
      });
    },
    getMediaThumbnail(media) {
      return new MediaThumbnailGenerator(media).getThumbnail();
    },
    visitMedia(m) {
      let type = m.type === 'collection' ? 'collections' : 'media';
      let networkSlug = this.network && this.network.slug !== undefined ? '/networks/' + this.network.slug + '/' : '/networks/';
      if (m.permission === 'authorized' && !this.$auth.auth) {
        this.$bus.emit('show-sign-in', m.url.view, this.$t('auth.signInMessage.protectedMedia'));
      } else {
        let route = networkSlug + type + '/' + m.slug;
        window.open(route, '_blank');
      }
    },
    downloadFile(link) {
      let msg = this.$t('network.downloadFileAlert');
      let action = () => {
        window.open(link, '_blank');
      };
      this.showConfirmPrompt(action, msg);
    },
    formatDate(date) {
      return moment(date).format('MM-DD-YYYY');
    },
    durationForHumans(seconds) {
      return durationForHumans(seconds);
    },
    goToEdit(media) {
      if (media.isCollection) {
        let link = "/networks/collections/" + media.slug + "/edit";
        window.open(link, '_blank');
      } else {
        if (this.network) {
          let link = "/networks/" + this.network.slug + "/media/" + media.slug + "/edit";
          window.open(link, '_blank');
        } else {
          let link = "/media/" + media.slug + "/edit";
          window.open(link, '_blank');
        }
      }
    },
  },
};
</script>
<style>
.media-card {
  top: 0px;
  visibility: visible;
  left: 0px;
  opacity: 1;
  transition-duration: 250ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: transform, opacity;
}
.media-card-thumbnail {
  height: 150px;
  text-decoration: none;
}
.media-card-thumbnail .card-img-top {
  height: fit-content;
}
.card-img-top:hover {
  text-decoration: none;
}
.media-card-section {
  min-height: 440px;
}
.presenter-facepile-text {
  font-size: 0.7rem;
  line-height: 1.2;
  letter-spacing: inherit;
  font-family: inherit;
  margin-bottom: unset;
}
/*Avatar*/
.avatar {
  font-size: 1rem;
  position: relative;
  display: inline-block;
  width: 3.875rem;
  height: 3.875rem;
}
.avatar-small {
  width: 1.875rem;
  height: 1.875rem;
}
.avatar .avatar-img {
  width: 100%;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
}
.avatar .avatar-text {
  display: block;
  width: 100%;
  height: 100%;
  position: relative;
  text-transform: uppercase;
  overflow: hidden;
  background: #fff;
  font-weight: 600;
  color: #00acf0;
}
.avatar .avatar-text .initial-wrap {
  display: table;
  width: 100%;
  height: 100%;
}
.avatar .avatar-text .initial-wrap > span {
  position: relative;
  display: table-cell;
  vertical-align: middle;
  text-align: center;
}
.avatar .avatar-text:before {
  content: '';
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  background: rgba(178, 230, 250, 0.5);
}
.avatar .avatar-text.avatar-text-indigo {
  color: #3a55b1;
}
.avatar .avatar-text.avatar-text-indigo:before {
  background: rgba(196, 204, 231, 0.5);
}
.avatar-group {
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
}
.avatar-group .avatar .avatar-img,
.avatar-group .avatar .avatar-text {
  border: 2px solid #fff;
}
.avatar-group.avatar-group-overlapped .avatar {
  margin-right: -13px;
}
.avatar-group.avatar-group-overlapped .avatar:hover {
  z-index: 1;
}
.avatar-group.avatar-group-lg .avatar {
  font-size: 0.95rem;
  width: 3.25rem;
  height: 3.25rem;
}
.avatar-group.avatar-group-lg.avatar-group-overlapped .avatar {
  margin-right: -15px;
}
.avatar-group.avatar-group-sm .avatar {
  font-size: 0.6rem;
  width: 2rem;
  height: 2rem;
}
.avatar-group.avatar-group-sm.avatar-group-overlapped .avatar {
  margin-right: -10px;
}
.avatar-group.avatar-group-overlapped .avatar {
  z-index: 1;
}
.avatar-group.avatar-group-overlapped .avatar:first-child {
  z-index: 2;
}
.avatar-group.avatar-group-overlapped .avatar:last-child {
  z-index: 0;
}
.dropbtn {
  padding: 0px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}
.dropbtn:hover {
  padding: 0px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}
.dropdown {
  position: relative;
}
.dropdown-content {
  position: absolute;
  background-color: #f9f9f9;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 98;
  max-height: 0;
  min-width: 250px;
  transition: max-height 0.15s ease-out;
  overflow: hidden;
}
.dropdown-down {
  bottom: 100%;
}
.dropdown-up {
  bottom: unset;
}
.dropdown-content a {
  color: black;
  background-color: #f9f9f9;
  padding: 12px 16px;
  text-decoration: none;
  display: block;
}
.dropdown-content a:hover {
  background-color: #e2e2e2;
}
.dropdown:hover .dropdown-content {
  max-height: 300px;
  min-width: 250px;
  width: 250px;
  margin-top: 5%;
  transition: max-height 0.25s ease-in;
  overflow: hidden;
  overflow-y: scroll;
}
.dropdown:hover .dropbtn {
  transition: max-height 0.25s ease-in;
}
.cursor-default {
  cursor: default;
}
.min-height-50 {
  min-height: 50px;
}
.min-height-65 {
  min-height: 65px;
}
.media-card-footer {
  background-color: var(--bs-light);
}
.media-card-header-text {
  margin-bottom: 5px;
  padding-bottom: 5px;
  max-height: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  content: '';
  position: relative;
  min-width: 89%;
}
.media-card-header-text:hover {
  max-height: 200px;
  min-height: 105px;
  text-overflow: ellipsis;
  background: #fff;
  position: absolute;
  box-shadow: 2px 2px 10px #ccc;
  border-radius: 6px;
  max-width: 89%;
}
.media-card-header-text:hover:before {
  background: none;
}
.media-card-header-text:before {
  content: '';
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  background: linear-gradient(180deg, transparent 63%, white 108%);
}
.hovered:hover {
  color: #005f9a !important;
}
@media (min-width: 1570px) {
  .custom-margin-xl-multiple {
    margin-left: -15px;
  }
  .custom-margin-xl-single {
    margin-left: -5px;
  }
}
</style>
<style class="scoped">
.avatar-group .avatar {
  font-size: 0.9rem !important;
  width: 2.625rem !important;
  height: 2.625rem !important;
}
</style>